/**
 * Meter
 * Classification: custom
 *
 * Radial progress ring for scores and grades. Replaces three divergent gauge
 * implementations. No ECharts dependency.
 */
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { chartTokens } from '@/tokens/chart';
import { severityColorsByMode, type SeverityKey } from '@/tokens/colors';
import { describeMeterArc } from './meterGeometry';

export interface MeterProps {
  /** Percentage, 0-100. Out-of-range and non-finite values are clamped. */
  value: number;
  /** Text rendered inside the ring. Defaults to the rounded value. */
  displayValue?: string;
  /** Accessible name, e.g. "Detection coverage". */
  label: string;
  /** A categorical series slot index (0-6), or a severity key. */
  tone?: number | SeverityKey;
  size?: number;
  strokeWidth?: number;
}

const VIEWBOX = 80;
const RADIUS = 28;

export function Meter({
  value,
  displayValue,
  label,
  tone = 0,
  size = 80,
  strokeWidth = 4,
}: MeterProps) {
  const theme = useTheme();
  const mode = theme.palette.mode === 'light' ? 'light' : 'dark';
  const tokens = chartTokens[mode];

  const color =
    typeof tone === 'number'
      ? tokens.series[tone % tokens.series.length]
      : severityColorsByMode[mode][tone];

  const { circumference, offset } = describeMeterArc(value, { radius: RADIUS, strokeWidth });
  const safe = Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;

  return (
    <Box
      sx={{ width: size, height: size, position: 'relative' }}
      role="meter"
      aria-valuenow={safe}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <svg viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} width="100%" height="100%" aria-hidden="true">
        <circle
          cx={VIEWBOX / 2}
          cy={VIEWBOX / 2}
          r={RADIUS}
          fill="none"
          stroke={tokens.ink.grid}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={VIEWBOX / 2}
          cy={VIEWBOX / 2}
          r={RADIUS}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${VIEWBOX / 2} ${VIEWBOX / 2})`}
        />
      </svg>
      <Typography
        variant="h6"
        component="span"
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          color: 'text.primary',
          fontSize: size / 3.5,
          lineHeight: 1,
        }}
      >
        {displayValue ?? Math.round(safe)}
      </Typography>
    </Box>
  );
}
